import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild, OnChanges, ChangeDetectorRef } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { TextCustomization } from '../services/text-customization.service';
import { ActionTable, ColumnsTable} from '../models/table.model';
import { parseDate } from 'ngx-bootstrap/chronos';
import { FilterTable } from '../models/filter.model';
import { BaseFormComponent } from '../../base/components/base-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MaskService } from '../services/mask.service';
import { FilterComponent } from "../filter/filter.component";
import { UtilService } from '../services/util.service';


@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent<T> extends BaseFormComponent<T> implements OnInit {
  totalRecords: number = 0;
  pageIndex: number = 0;
  inputFilterData: string = '';
  filteredListData:any[] = [];
  showFilter:boolean = false;
  currentFilter: any = [];

  @Input() fileName: string = '';
  @Input() title: string = '';
  @Input() canCreate: boolean = true;
  @Input() canChange: boolean = true;
  @Input() canView: boolean = true;
  @Input() canPaginate: boolean = true;
  @Input() canExport: boolean = true;
  @Input() canShowSum: boolean = false;
  @Input() sumLabelKey: string = '';
  @Input() permissionKey: string = '';
  @Input() hasCheckColumn: boolean = false;
  @Input() contentInHeader: boolean = false;
  @Input() filterField: string = '';
  @Input() filterForm: FilterTable[] = [];
  @Input() pageSize: number = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 20, 50, 100];
  @Input() listColumns: ColumnsTable[] = [];
  @Input() listData:any[] = [];
  @Input() listActions: ActionTable[] = [];
  @Output() formDataChange= new EventEmitter<FormGroup>();

  displayedColumns: string[] = [];

  private paginatorInitialized = false;

  @ViewChild('paginator') paginator!: MatPaginator;

  @ViewChild('paginatorFooter') set matPaginator(paginator: MatPaginator) {
    if (paginator && !this.paginatorInitialized) {
      this.paginatorInitialized = true;
      this.textCustomization.changePaginatorText(paginator._intl);
    }
  }

  constructor(
    private textCustomization: TextCustomization,
    public acRoute: ActivatedRoute,
    private maskService:MaskService,
    private router: Router,
    private util: UtilService,
    private cdRef: ChangeDetectorRef
  ) {
    super(acRoute);
  }

  ngOnInit() {
    this.getQueryPagination();
    this.filteredListData = this.listData;
    this.initForm();
    this.filterData([], true);
    var hasInput = this.listColumns.some(c => c.type == 'input');
    if(this.hasCheckColumn || hasInput){
      this.form.valueChanges.subscribe(value => {
        this.formDataChange.emit(this.form);//value?.checks ?? []
      });
    }
    this.checkQueryFilters();
  }

  initForm():void{
    var inputItems = this.listColumns.filter(c => c.type == 'input').map(f =>{
      const validators:any[] = [];
      if (f.required) validators.push(Validators.required);
      if (f.maxLength) validators.push(Validators.maxLength(f.maxLength));
      if (f.minLength) validators.push(Validators.minLength(f.minLength));
      if (f.min !== undefined && f.min !== null) validators.push(Validators.min(f.min));
      if (f.max !== undefined && f.max !== null) validators.push(Validators.max(f.max));
      return {...f, validators};
    })
    this.form = new FormGroup({
      data: new FormArray(
        this.listData?.map(item => {
          const formGroupConfig: { [key: string]: FormControl } = {};
          inputItems.forEach(input => {
            formGroupConfig[input.id] = new FormControl(item[input.id], input.validators);
          });
          return new FormGroup({
            id: new FormControl(item.id),
            tempId: new FormControl(item.tempId),
            status: new FormControl(false),
            ...formGroupConfig
          })
        }) ?? []
      )
    });
    
    if (this.canShowSum) {
      (this.form.get('data') as FormArray).push(
        new FormGroup({
          id: new FormControl(null),
          tempId: new FormControl(null),
          status: new FormControl(false),
        })
      );
    }
  }

  ngOnChanges(changes : SimpleChanges) {
    this.getQueryPagination();
    if (changes['listData']) {
      this.showFilter = false;
      this.listData = changes['listData'].currentValue;
      this.filteredListData = this.listData;
      this.totalRecords = this.listData?.length ?? 0;
      this.filterData([], false);
      this.initForm();
      var hasInput = this.listColumns.some(c => c.type == 'input');
      if(this.hasCheckColumn || hasInput){
        this.form.valueChanges.subscribe(value => {
          this.formDataChange.emit(this.form);
        });
      }
      this.checkQueryFilters();
    }
  }

  pageChangeEvent(event: PageEvent):void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.savePaginationToQueryParams();
    this.filterData(this.currentFilter);
  }

  filterData(listFilterData: any, resetCount:boolean = false, isExport:boolean = false):any {
    this.loading();
    this.currentFilter = listFilterData;
    var filters = Object.values(listFilterData) as any;
    
    var filterData = this.listData?.filter((item: Record<string, any>) =>{
      return filters.every((filter: Record<string, any>) => {
          if(item[filter['key']] && filter['value']){
            if(filter['type'] == 'string'){
              return item[filter['key']]?.toLowerCase()?.includes(filter['value'].toLowerCase());
            }
            else if(filter['type'] == 'select'){
              return item[filter['key']] == filter['value'];
            }
            else if(filter['type'] == 'rangeDate'){
              const startDate = parseDate(filter['start']);
              const endDate = parseDate(filter['end']);

              const itemStart = parseDate(item[filter['key_start']]);
              const itemEnd = parseDate(item[filter['key_end']]);
              return ( !filter['start'] ||  startDate <= itemStart ) && 
                      ( !filter['end'] || endDate >= itemEnd);
            }
            else if(filter['type'] == 'cpf'){
              return item[filter['key']] === filter['value']?.replaceAll('.', '').replaceAll('-', '');
            }
            return item[filter['key']] === filter['value'];
          }
          return true
        }
      )
    });
    if(!isExport){
      var hasInput = this.listColumns.some(c => c.type == 'input');
      if(this.hasCheckColumn || hasInput){
        this.initForm();
      }
      if(this.canPaginate){
        if (resetCount){
          this.pageIndex = 0;
          this.paginator?.firstPage();
        }
        var start = this.pageIndex * this.pageSize;
        var end = start + this.pageSize;
        if(this.canShowSum){
          filterData.push(this.sumAllKeys(filterData));
        }
        this.filteredListData = filterData?.slice(start,end);
        this.totalRecords = filterData?.length ?? 0;
      }
      this.loaded();
    }
    else{
      return filterData;
    }
    
  }

  formatValue(value: any, column: ColumnsTable): any {
    switch(column.type){
      case 'date':
        if (value == null || value == '') return  '';
        return new Date(value).toLocaleDateString('pt-BR');
      case 'select':
        var option = column?.options?.find((o) => o['id'] == value);
        return option?.['label'] ?? '';
      case 'boolean':
        return value === true? 'Sim': (value === false? 'Não':'');
      case 'percent':
        return value + '%';
      default:
        return value;
    }
  }
  

  changeModal(status: boolean):void{
    this.showFilter = status;
  }

  changeAll(event:Event):void{
    const status = (event.target as HTMLInputElement).checked;
    const formArray = this.form.get('data') as FormArray;
    formArray.controls.forEach(control =>
      (control as FormGroup).get('status')?.setValue(status)
    );
    this.markAsDirty();
  }

  exportData(): void{
    this.loading();
    const fileName = this.fileName ?? this.title + '.csv';
    const data = this.filterData(this.currentFilter, false, true);
    this.util.exportToCSV(data, fileName);
    this.loaded();
  }
  
  navigate(id:number, key:string): void{
    this.router.navigate([key + '/' + id], { relativeTo: this.acRoute });
  }

  get hasAction(){
    return this.listActions.length > 0 ||
      ( this.canChange )  ||
      ( this.canView );
  }

  canShow(item:any){
    const canChange = item.podeSerAlterada === false? false: true;
    return (!canChange)  && this.canView;
  }

  canEdit(item:any){
    const canChange = item.podeSerAlterada === false? false: true;
    return  canChange && this.canChange;
  }

  maskMoney(key:string, index: number,event:Event){
    var value = (event.target as HTMLInputElement).value;
    var newValue = this.maskService.maskMoney(value);
    (this.form.get('data') as FormArray).at(index).get(key)?.setValue(newValue);
  }

  maskInteger(key:string, index: number, event:Event){
    var value = (event.target as HTMLInputElement).value;
    var newValue = this.maskService.maskInteger(value);
    (this.form.get('data') as FormArray).at(index).get(key)?.setValue(newValue);
  }

  sumAllKeys(listData:any[]): { [key: string]: number } {
    const totals: { [key: string]: any } = {};
    listData.forEach(item => {
      Object.keys(item).forEach(key => {
        const value = item[key as keyof typeof item];
        if (typeof value === 'number') {
          totals[key] = (totals[key] || 0) + value;
        }
      });
    });
    totals[this.sumLabelKey]= 'TOTAL';
    totals['class']= 'sumData';
    return totals;
  }

  checkQueryFilters(){
    if(this.filterForm.length > 0){
      this.acRoute.queryParams.subscribe(params => {
        var minParam = 0;
        if(Object.keys(params).includes('pageSize') && Object.keys(params).includes('pageIndex')){
          minParam = 2;
        }
        if (Object.keys(params).length > minParam) {
          setTimeout(() => {
            this.showFilter = true;
            this.cdRef.detectChanges();
          }, 0)
        }
      });
    }
  }
  getQueryPagination(){
    this.acRoute.queryParams.subscribe(params => {
      if (params) {
        try {
          this.pageIndex = +(params['pageIndex'] ?? this.pageIndex);
          this.pageSize = +(params['pageSize'] ?? this.pageSize);
        } catch (e) {
          console.error('Erro ao aplicar filtro dos query params', e);
        }
      }
    });
  }
  
  savePaginationToQueryParams() {
    this.router.navigate([], {
      relativeTo: this.acRoute,
      queryParams: { pageIndex: this.pageIndex, pageSize: this.pageSize},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
